import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Checkbox,
  TextField,
  CircularProgress,
  LinearProgress,
  Paper,
  Alert,
  Tooltip,
} from '@mui/material';
import { Plus, Trash2, CheckSquare, Square, GripVertical } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { taskService } from '@/services/api';
import { QUERY_KEYS } from '@/services/queryClient';
import { ChecklistItem } from '@/types';
import { useSnackbar } from '@/shared/contexts/SnackbarContext';

interface TaskChecklistTabProps {
  taskId: string;
  canEdit?: boolean;
}

export const TaskChecklistTab: React.FC<TaskChecklistTabProps> = ({ taskId, canEdit = true }) => {
  const [newItemLabel, setNewItemLabel] = useState('');
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [editingLabel, setEditingLabel] = useState('');
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  // Fetch checklist items
  const { data, isLoading, error } = useQuery({
    queryKey: QUERY_KEYS.taskChecklist(taskId),
    queryFn: () => taskService.getChecklistItems(taskId),
    enabled: !!taskId,
  });

  const checklistItems = data?.items ?? [];
  const progress = data?.progress ?? { completed: 0, total: 0 };

  // Add item mutation
  const addItemMutation = useMutation({
    mutationFn: (label: string) => taskService.addChecklistItem(taskId, label),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.taskChecklist(taskId) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.task(taskId) });
      setNewItemLabel('');
      showSnackbar('Checklist item added', 'success');
    },
    onError: (error: any) => {
      showSnackbar(error?.response?.data?.message ?? 'Failed to add item', 'error');
    },
  });

  // Update item mutation
  const updateItemMutation = useMutation({
    mutationFn: ({ itemId, updates }: { itemId: string; updates: { label?: string; isChecked?: boolean } }) =>
      taskService.updateChecklistItem(taskId, itemId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.taskChecklist(taskId) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.task(taskId) });
      setEditingItemId(null);
    },
    onError: (error: any) => {
      showSnackbar(error?.response?.data?.message ?? 'Failed to update item', 'error');
    },
  });

  // Delete item mutation
  const deleteItemMutation = useMutation({
    mutationFn: (itemId: string) => taskService.deleteChecklistItem(taskId, itemId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.taskChecklist(taskId) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.task(taskId) });
      showSnackbar('Checklist item deleted', 'success');
    },
    onError: (error: any) => {
      showSnackbar(error?.response?.data?.message ?? 'Failed to delete item', 'error');
    },
  });

  // Handlers
  const handleAddItem = () => {
    const label = newItemLabel.trim();
    if (!label) {
      showSnackbar('Please enter a label', 'warning');
      return;
    }
    addItemMutation.mutate(label);
  };

  const handleToggleCheck = (item: ChecklistItem) => {
    if (!canEdit) return;
    updateItemMutation.mutate({
      itemId: item.id,
      updates: { isChecked: !item.isChecked },
    });
  };

  const handleDeleteItem = (itemId: string) => {
    if (!canEdit) return;
    deleteItemMutation.mutate(itemId);
  };

  const handleStartEdit = (item: ChecklistItem) => {
    setEditingItemId(item.id);
    setEditingLabel(item.label);
  };

  const handleSaveEdit = (itemId: string) => {
    const label = editingLabel.trim();
    if (!label) {
      showSnackbar('Label cannot be empty', 'warning');
      return;
    }
    updateItemMutation.mutate({
      itemId,
      updates: { label },
    });
  };

  const handleCancelEdit = () => {
    setEditingItemId(null);
    setEditingLabel('');
  };

  const progressPercentage = progress.total > 0 ? (progress.completed / progress.total) * 100 : 0;

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Failed to load checklist items
      </Alert>
    );
  }

  return (
    <Box sx={{ py: 2 }}>
      {/* Progress Bar */}
      {progress.total > 0 && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
            <Typography variant="body2" color="text.secondary">
              Progress
            </Typography>
            <Typography variant="body2" fontWeight={500}>
              {progress.completed} / {progress.total} completed
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={progressPercentage}
            sx={{
              height: 8,
              borderRadius: 4,
              backgroundColor: '#e0e0e0',
              '& .MuiLinearProgress-bar': {
                backgroundColor: progressPercentage === 100 ? '#10b981' : '#4F46E5',
                borderRadius: 4,
              },
            }}
          />
        </Paper>
      )}

      {/* Add New Item */}
      {canEdit && (
        <Box display="flex" gap={1} mb={2}>
          <TextField
            fullWidth
            size="small"
            placeholder="Add new checklist item..."
            value={newItemLabel}
            onChange={(e) => setNewItemLabel(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAddItem();
              }
            }}
            disabled={addItemMutation.isPending}
          />
          <Button
            variant="contained"
            startIcon={<Plus size={16} />}
            onClick={handleAddItem}
            disabled={addItemMutation.isPending || !newItemLabel.trim()}
            sx={{ minWidth: 100 }}
          >
            Add
          </Button>
        </Box>
      )}

      {/* Checklist Items */}
      {checklistItems.length === 0 ? (
        <Box
          sx={{
            textAlign: 'center',
            py: 6,
            color: 'text.secondary',
          }}
        >
          <Square size={48} style={{ opacity: 0.3, margin: '0 auto 16px' }} />
          <Typography variant="body2">No checklist items yet</Typography>
          {canEdit && (
            <Typography variant="caption" color="text.secondary">
              Add items to break down this task into smaller steps
            </Typography>
          )}
        </Box>
      ) : (
        <List sx={{ bgcolor: 'background.paper', borderRadius: 1 }}>
          {checklistItems.map((item, index) => (
            <ListItem
              key={item.id}
              sx={{
                borderBottom: index < checklistItems.length - 1 ? '1px solid #e5e7eb' : 'none',
                '&:hover': {
                  bgcolor: canEdit ? '#f9fafb' : 'transparent',
                },
              }}
              disablePadding
            >
              {/* Drag Handle (for future reordering) */}
              {canEdit && (
                <ListItemIcon sx={{ minWidth: 32, cursor: 'grab' }}>
                  <GripVertical size={16} style={{ color: '#9ca3af' }} />
                </ListItemIcon>
              )}

              {/* Checkbox */}
              <ListItemIcon sx={{ minWidth: 40 }}>
                <Checkbox
                  checked={item.isChecked}
                  onChange={() => handleToggleCheck(item)}
                  disabled={!canEdit || updateItemMutation.isPending}
                  icon={<Square size={20} />}
                  checkedIcon={<CheckSquare size={20} style={{ color: '#10b981' }} />}
                />
              </ListItemIcon>

              {/* Label */}
              <ListItemText
                sx={{
                  flex: 1,
                  '& .MuiListItemText-primary': {
                    textDecoration: item.isChecked ? 'line-through' : 'none',
                    color: item.isChecked ? 'text.secondary' : 'text.primary',
                  },
                }}
              >
                {editingItemId === item.id ? (
                  <Box display="flex" gap={1} alignItems="center">
                    <TextField
                      size="small"
                      fullWidth
                      value={editingLabel}
                      onChange={(e) => setEditingLabel(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleSaveEdit(item.id);
                        } else if (e.key === 'Escape') {
                          handleCancelEdit();
                        }
                      }}
                      autoFocus
                    />
                    <Button size="small" onClick={() => handleSaveEdit(item.id)}>
                      Save
                    </Button>
                    <Button size="small" onClick={handleCancelEdit}>
                      Cancel
                    </Button>
                  </Box>
                ) : (
                  <Typography
                    variant="body2"
                    onDoubleClick={() => canEdit && handleStartEdit(item)}
                    sx={{ cursor: canEdit ? 'text' : 'default' }}
                  >
                    {item.label}
                  </Typography>
                )}
              </ListItemText>

              {/* Actions */}
              {canEdit && editingItemId !== item.id && (
                <ListItemSecondaryAction>
                  <Tooltip title="Delete item">
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={() => handleDeleteItem(item.id)}
                      disabled={deleteItemMutation.isPending}
                      sx={{ color: '#ef4444' }}
                    >
                      <Trash2 size={16} />
                    </IconButton>
                  </Tooltip>
                </ListItemSecondaryAction>
              )}
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  );
};

export default TaskChecklistTab;
